<template>
  <svg
    ref="badgeRef"
    data-testid="team-badge"
    :class="[
      layoutClass,
      classes.imageWrapper,
      'team-badge',

      {
        'default-badge': !src
      }
    ]"
    viewBox="0 0 100 100"
    xmlns="http://www.w3.org/2000/svg"
    preserveAspectRatio="xMidYMid meet"
  >
    <defs>
      <filter id="inset-shadow">
        <!-- Shadow offset -->
        <feOffset dx="0" dy="0" />
        <!-- Shadow blur -->
        <feGaussianBlur
          stdDeviation="10"
          color-interpolation-filters="sRGB"
          result="offset-blur"
        />
        <!-- Invert drop shadow to make an inset shadow-->
        <feComposite
          operator="out"
          in="SourceGraphic"
          in2="offset-blur"
          result="inverse"
        />
        <!-- Cut colour inside shadow -->
        <feFlood flood-color="black" flood-opacity="0.7" result="color" />

        <!-- Placing shadow over element -->
        <feComposite operator="in" in="color" in2="inverse" result="shadow" />
        <feComposite operator="over" in="shadow" in2="SourceGraphic" />
        <feComposite in2="SourceGraphic" operator="over" />
      </filter>
    </defs>
    <image
      :filter="isCountryCodeImg ? 'url(#inset-shadow)' : ''"
      :x="isCountryCodeImg ? '0' : !src ? '7%' : '15%'"
      :y="isCountryCodeImg ? '0' : !src ? '9%' : '14%'"
      :height="isCountryCodeImg ? '100%' : !src ? '85%' : '70%'"
      :href="imageSrc"
      :alt="alt"
      data-testid="team-badge-image"
    />
  </svg>
</template>
<script lang="ts">
import { injectBrand } from "@fresh8/ad-data-composition/src/brand";
import { createUseStyles } from "@fresh8/vue-jss/src";

import { computed, defineComponent, PropType, ref, onBeforeMount } from "vue";
import { injectTemplateSize } from "@fresh8/ad-data-composition/src/useTemplateSize";
import { injectTeamColors } from "../lib/teamBranding";

const useStyles = createUseStyles({
  imageWrapper: {
    width: "100%",
    boxSizing: "border-box",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderStyle: "solid",
    borderWidth: props => props.borderWidth,
    background: "white",
    borderRadius: "9001px",
    margin: "0",
    borderColor: props => props.borderColor,
    boxShadow: props =>
      !props.hideBoxShadow
        ? "0 2px 8px -2px rgba(0,0,0,0.16), 0 1px 2px rgba(0,0,0,.3)"
        : // ? "0 2px 8px -2px rgba(0,0,0,0.16), 0 1px 2px rgba(0,0,0,.3),inset 0 0px 0px 10px rgba(0,0,0,0.7)"
          "",
    maxWidth: "32px"
  }
});

export default defineComponent({
  name: "TeamBadge",
  props: {
    defaultBadge: {
      type: String as PropType<"home" | "away">,
      default: "home"
    },
    src: {
      type: String,
      required: false,
      default: ""
    },
    alt: {
      type: String,
      required: false,
      default: ""
    },
    countryCode: {
      type: String,
      required: false,
      default: undefined
    },
    borderWidth: {
      type: String,
      required: false,
      default: "1px"
    },
    hideBoxShadow: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  setup(props) {
    const brandContext = injectBrand();
    const hasSize = ref(true);
    const teamColorsContext = injectTeamColors();

    const countryCodeSrc = computed(() => {
      return props.countryCode
        ? `https://cdn.fresh8.co/assets/flags/${props.countryCode.toLocaleLowerCase()}.svg`
        : undefined;
    });

    const images = {
      home:
        "https://cdn.fresh8.co/assets/mercury-assets/team-branding-badges/generic-badge-1.png",
      away:
        "https://cdn.fresh8.co/assets/mercury-assets/team-branding-badges/generic-badge-2.png"
    };

    const template = injectTemplateSize();

    const teamColours = computed(
      () => teamColorsContext.colors.value[props.defaultBadge]
    );

    const srcImage = computed(() => {
      return countryCodeSrc.value || props.src;
    });

    const isValidImage = () => {
      const img = new Image();
      img.onload = () => {
        hasSize.value = img.height > 1 && img.width > 1;
        img.remove();
      };
      img.src = srcImage.value || "";
    };

    onBeforeMount(() => {
      isValidImage();
    });

    const imageSrc = computed(() => {
      return hasSize.value && srcImage.value
        ? srcImage.value
        : images[props.defaultBadge];
    });

    const borderColor = computed(() => {
      if (teamColorsContext.teamColorsAvailable.value) {
        return teamColours.value?.banner.background;
      } else {
        return brandContext?.teamBranding.value.badge.borderColor;
      }
    });

    const reactiveStyles = computed(() => ({
      borderWidth: props.borderWidth,
      borderColor: borderColor.value,
      hideBoxShadow: props.hideBoxShadow
    }));

    const classes = useStyles(reactiveStyles);
    return {
      imageSrc,
      classes,
      isCountryCodeImg: computed(() => !!countryCodeSrc.value),
      layoutClass: computed(() => template?.size.value?.name)
    };
  }
});
</script>
